/** Canonical ticket workflow statuses (internal slug values; API uses dashes via `statusToApiValue`). */
export const TICKET_STATUS_OPTIONS = [
  { value: "fixed_in_development", label: "Fixed in Development" },
  { value: "ready_for_qa", label: "Ready for QA" },
  { value: "close", label: "Close" },
  { value: "on_hold", label: "On Hold" },
  { value: "new", label: "New" },
  { value: "moved_to_production", label: "Move to Production" },
  { value: "staging_update", label: "Staging Updated" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending_from_client", label: "Pending From Client" },
  { value: "local_updated", label: "Local Updated" },
  { value: "feedback", label: "Feedback" },
  { value: "reopen", label: "Reopen" },
] as const;

export type TicketStatus = (typeof TICKET_STATUS_OPTIONS)[number]["value"];

/**
 * Dashboard Project Status cards — workflow order (product spec).
 * Other screens keep `TICKET_STATUS_OPTIONS` order unchanged.
 */
export const STATUS_DASHBOARD_DISPLAY_ORDER = [
  "new",
  "in_progress",
  "fixed_in_development",
  "ready_for_qa",
  "reopen",
  "local_updated",
  "staging_update",
  "moved_to_production",
  "close",
  "on_hold",
  "feedback",
  "pending_from_client",
] as const satisfies readonly TicketStatus[];

export type WorkflowTicketStatus =
  (typeof STATUS_DASHBOARD_DISPLAY_ORDER)[number];

const STATUS_OPTION_BY_VALUE = Object.fromEntries(
  TICKET_STATUS_OPTIONS.map(opt => [opt.value, opt])
) as Record<TicketStatus, (typeof TICKET_STATUS_OPTIONS)[number]>;

export const STATUS_DASHBOARD_TICKET_STATUS_OPTIONS =
  STATUS_DASHBOARD_DISPLAY_ORDER.map(
    value => STATUS_OPTION_BY_VALUE[value]
  );

/**
 * Editable workflow statuses for status dropdowns, status filters, and Kanban columns.
 * Same set as dashboard (incl. `pending_from_client`) — PATCH / filter / GET use dashes via `statusToApiValue`.
 */
export const TICKET_STATUS_WORKFLOW_ORDER = [
  "new",
  "in_progress",
  "fixed_in_development",
  "ready_for_qa",
  "reopen",
  "local_updated",
  "staging_update",
  "moved_to_production",
  "close",
  "on_hold",
  "feedback",
  "pending_from_client",
] as const satisfies readonly TicketStatus[];

export const TICKET_STATUS_WORKFLOW_OPTIONS = TICKET_STATUS_WORKFLOW_ORDER.map(
  value => STATUS_OPTION_BY_VALUE[value]
);

/** Status filters and change dropdowns — product workflow order. */
export const TICKET_STATUS_FILTER_OPTIONS = TICKET_STATUS_WORKFLOW_OPTIONS;

/** Retired slugs — never shown in dropdowns or filter chips. */
export const RETIRED_TICKET_STATUS_SLUGS = new Set(["pending", "resolved", "fixed", "closed"]);

export function isRetiredTicketStatus(raw: unknown): boolean {
  return RETIRED_TICKET_STATUS_SLUGS.has(statusToSlug(raw));
}

/** Every status `<Select>` / filter uses this list only. */
export const TICKET_STATUS_DROPDOWN_OPTIONS = TICKET_STATUS_FILTER_OPTIONS.filter(
  opt => !isRetiredTicketStatus(opt.value)
);

/**
 * Agent panel — status *change* dropdown only (not filters / Kanban columns).
 * Super Agent and Admin keep the full workflow list.
 */
export const AGENT_STATUS_CHANGE_VALUES = [
  "in_progress",
  "fixed_in_development",
  "ready_for_qa",
  "local_updated",
] as const satisfies readonly TicketStatus[];

export const AGENT_STATUS_CHANGE_OPTIONS = AGENT_STATUS_CHANGE_VALUES.map(
  value => STATUS_OPTION_BY_VALUE[value]
);

/**
 * Agent-shell status dropdown must include the ticket's current status so
 * Radix Select is not blank when the ticket is New / Close / etc.
 */
export function agentStatusSelectOptions(current: unknown): {
  value: TicketStatus;
  label: string;
}[] {
  const currentSlug = statusForSelect(current);
  if (
    AGENT_STATUS_CHANGE_VALUES.includes(
      currentSlug as (typeof AGENT_STATUS_CHANGE_VALUES)[number]
    )
  ) {
    return [...AGENT_STATUS_CHANGE_OPTIONS];
  }
  const currentOption = STATUS_OPTION_BY_VALUE[currentSlug];
  if (!currentOption) return [...AGENT_STATUS_CHANGE_OPTIONS];
  return [currentOption, ...AGENT_STATUS_CHANGE_OPTIONS];
}

/** Map API / legacy values to a valid dropdown value. */
export function statusForSelect(raw: unknown): TicketStatus {
  return normalizeStatus(raw);
}

/**
 * Convert an internal status slug to the value the backend expects in payloads.
 * Backend uses dashes, not underscores (e.g. `in_progress` → `in-progress`,
 * `ready_for_qa` → `ready-for-qa`, `close` → `close`).
 */
export function statusToApiValue(raw: unknown): string {
  return normalizeStatus(raw).replace(/_/g, "-");
}

/** Remove retired statuses from multi-select filter state. */
export function sanitizeStatusFilterValues(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    if (isRetiredTicketStatus(raw)) continue;
    const normalized = normalizeStatus(raw);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

export const TICKET_STATUSES: TicketStatus[] = TICKET_STATUS_OPTIONS.map(
  o => o.value
);

export const DEFAULT_TICKET_STATUS: TicketStatus = "new";

export const STATUS_LABELS: Record<TicketStatus, string> = Object.fromEntries(
  TICKET_STATUS_OPTIONS.map(o => [o.value, o.label])
) as Record<TicketStatus, string>;

/** Sidebar queue id → API status filter value. */
export const QUEUE_STATUS_FILTER: Partial<Record<string, TicketStatus>> = {
  closed: "close",
};

const LEGACY_STATUS_ALIASES: Record<string, TicketStatus> = {
  open: "new",
  closed: "close",
  close: "close",
  active: "in_progress",
  inprogress: "in_progress",
  "in-progress": "in_progress",
  hold: "on_hold",
  waiting: "pending_from_client",
  pending: "pending_from_client",
  resolved: "close",
  done: "close",
  complete: "close",
  completed: "close",
  cancelled: "close",
  canceled: "close",
  archived: "close",
  feedback: "feedback",
  reopen: "reopen",
  reopened: "reopen",
  fixed: "ready_for_qa",
  ready_for_qa: "ready_for_qa",
  fixed_in_developer: "fixed_in_development",
  fixed_in_development: "fixed_in_development",
  moved_to_production: "moved_to_production",
  staging_update: "staging_update",
  pending_from_client: "pending_from_client",
  local_updated: "local_updated",
  on_hold: "on_hold",
  in_progress: "in_progress",
};

export function statusToSlug(raw: unknown): string {
  return String(raw ?? "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
}

export function isTicketStatus(value: string): value is TicketStatus {
  return (TICKET_STATUSES as string[]).includes(value);
}

export function normalizeStatus(raw: unknown): TicketStatus {
  const slug = statusToSlug(raw);
  if (!slug) return DEFAULT_TICKET_STATUS;
  if (isTicketStatus(slug)) return slug;
  if (slug in LEGACY_STATUS_ALIASES) return LEGACY_STATUS_ALIASES[slug]!;
  return DEFAULT_TICKET_STATUS;
}

export function formatStatusLabel(raw: unknown): string {
  const key = String(raw ?? "").trim();
  if (!key) return "—";
  const normalized = normalizeStatus(raw);
  return STATUS_LABELS[normalized] ?? normalized;
}

/** Shorter labels for STREAM / compact lists — full label in `title` tooltip. */
export function formatStatusLabelCompact(raw: unknown): string {
  const normalized = normalizeStatus(raw);
  const compact: Partial<Record<TicketStatus, string>> = {
    moved_to_production: "In Production",
    pending_from_client: "Pending Client",
    fixed_in_development: "Fixed in Dev",
    ready_for_qa: "Ready for QA",
    staging_update: "Staging",
    local_updated: "Local Updated",
    in_progress: "In Progress",
  };
  return compact[normalized] ?? formatStatusLabel(raw);
}

export const statusColors: Record<TicketStatus, string> = {
  fixed_in_development:
    "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
  ready_for_qa:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  close: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  on_hold:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  moved_to_production:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  staging_update:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
  in_progress:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
  pending_from_client:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  local_updated:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
  feedback:
    "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
  reopen:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

/** Project stats dashboard — light cards aligned with `statusColors` badges. */
export const statusDashboardCardTheme: Record<
  TicketStatus,
  { card: string; accent: string; label: string; count: string }
> = {
  fixed_in_development: {
    card: "border-violet-100 bg-violet-50/60",
    accent: "bg-violet-300",
    label: "text-violet-600",
    count: "text-violet-600",
  },
  ready_for_qa: {
    card: "border-green-100 bg-green-50/60",
    accent: "bg-green-300",
    label: "text-green-600",
    count: "text-green-600",
  },
  close: {
    card: "border-slate-200 bg-slate-50/80",
    accent: "bg-slate-300",
    label: "text-slate-600",
    count: "text-slate-600",
  },
  on_hold: {
    card: "border-orange-100 bg-orange-50/60",
    accent: "bg-orange-300",
    label: "text-orange-600",
    count: "text-orange-600",
  },
  new: {
    card: "border-blue-100 bg-blue-50/60",
    accent: "bg-blue-300",
    label: "text-blue-600",
    count: "text-blue-600",
  },
  moved_to_production: {
    card: "border-teal-100 bg-teal-50/60",
    accent: "bg-teal-300",
    label: "text-teal-600",
    count: "text-teal-600",
  },
  staging_update: {
    card: "border-cyan-100 bg-cyan-50/60",
    accent: "bg-cyan-300",
    label: "text-cyan-600",
    count: "text-cyan-600",
  },
  in_progress: {
    card: "border-sky-100 bg-sky-50/60",
    accent: "bg-sky-300",
    label: "text-sky-600",
    count: "text-sky-600",
  },
  pending_from_client: {
    card: "border-yellow-100 bg-yellow-50/60",
    accent: "bg-yellow-300",
    label: "text-yellow-600",
    count: "text-yellow-600",
  },
  local_updated: {
    card: "border-indigo-100 bg-indigo-50/60",
    accent: "bg-indigo-300",
    label: "text-indigo-600",
    count: "text-indigo-600",
  },
  feedback: {
    card: "border-pink-100 bg-pink-50/60",
    accent: "bg-pink-300",
    label: "text-pink-600",
    count: "text-pink-600",
  },
  reopen: {
    card: "border-amber-100 bg-amber-50/60",
    accent: "bg-amber-400",
    label: "text-amber-600",
    count: "text-amber-600",
  },
};

/** Light pastel chart palette — pink · blue · yellow · teal · purple · orange. */
export const CHART_PASTEL_FILLS = [
  "hsl(350 55% 82%)",
  "hsl(205 65% 80%)",
  "hsl(43 70% 82%)",
  "hsl(172 40% 78%)",
  "hsl(270 50% 84%)",
  "hsl(25 70% 82%)",
] as const;

export function chartPastelColor(index: number): string {
  return CHART_PASTEL_FILLS[index % CHART_PASTEL_FILLS.length]!;
}

/** Solid chart fills — cycles `CHART_PASTEL_FILLS`. */
export const statusChartFillColors: Record<TicketStatus, string> =
  Object.fromEntries(
    TICKET_STATUS_OPTIONS.map((opt, index) => [
      opt.value,
      chartPastelColor(index),
    ])
  ) as Record<TicketStatus, string>;

export const KANBAN_COLUMN_META: {
  id: TicketStatus;
  label: string;
  color: string;
}[] = TICKET_STATUS_WORKFLOW_OPTIONS.map(opt => ({
  id: opt.value,
  label: opt.label,
  color: "bg-muted/40 border-border/80",
}));
